import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 4
import { PanelFrame } from '../workspace/PanelFrame';
import {
  Activity, AlertTriangle, CheckCircle2, BookCheck, Gauge,
  Loader2, RefreshCw, BookOpen, Clock, TrendingUp, TrendingDown, Minus,
  Wrench, Users, GitBranch, Layers, MessageSquare, Flame,
  Sparkles, Zap, ArrowRight, Film,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { usePW } from '@/lib/store';
import { generateApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area,
  XAxis, YAxis, Tooltip, ReferenceLine,
  LineChart, Line,
} from 'recharts';
import PlotReadinessPill from './PlotReadinessPill';
import { IssueRepairModal } from './editor/IssueRepairModal';

export function AnalysisPanel({ onClose, maximized, onMaximize }) {
  const { chapters, activeChapterId, project } = usePW();
  const { t } = useTranslation();

  const [chapterResult, setChapterResult] = useState(null);
  const [novelResult,   setNovelResult]   = useState(null);
  const [runningChapter, setRunningChapter] = useState(false);
  const [runningNovel,   setRunningNovel]   = useState(false);
  const [repairOpen,     setRepairOpen]     = useState(false);
  const [repairIssues,   setRepairIssues]   = useState([]);

  const activeChapter = chapters.find(c => c.id === activeChapterId);

  // ── Auto-load cached results on mount / chapter switch ──────────────────
  useEffect(() => {
    if (!activeChapterId || !project?.id) return;
    generateApi.getAnalysis(project.id, activeChapterId)
      .then(data => { if (data) setChapterResult(data); })
      .catch(() => {});
  }, [activeChapterId, project?.id]);

  // ── Sentiment sparkline data for novel tab ───────────────────────────────
  // chapter_sentiments is a {chapter_id: score} dict from the API.
  // Convert to sorted array using the chapters list for correct ordering.
  const sentimentData = (() => {
    const raw = novelResult?.chapter_sentiments;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    return Object.entries(raw)
      .map(([cid, v]) => {
        const ch = chapters.find((c) => c.id === cid);
        return { ch: ch?.number ?? 0, label: `Ch ${ch?.number ?? '?'}`, v };
      })
      .sort((a, b) => a.ch - b.ch);
  })();

  const runChapterAnalysis = async () => {
    if (!activeChapterId) {
      toast.warning(t('analysis.openChapterFirst'));
      return;
    }
    if (!(activeChapter?.content || '').trim()) {
      toast.warning(t('analysis.noContent'));
      return;
    }
    setRunningChapter(true);
    try {
      const result = await generateApi.analyzeChapter(activeChapterId);
      setChapterResult({ ...result, analyzed_at: result.analyzed_at || new Date().toISOString() });
      toast.success(t('analysis.chapterComplete'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('analysis.analysisFailed'));
    } finally {
      setRunningChapter(false);
    }
  };

  const runNovelAnalysis = async () => {
    if (!project?.id) return;
    const hasProse = chapters.some(c => (c.content || '').trim());
    if (!hasProse) {
      toast.warning(t('analysis.noChapterContent'));
      return;
    }
    setRunningNovel(true);
    try {
      const result = await generateApi.analyzeNovel(project.id);
      setNovelResult({ ...result, analyzed_at: result.analyzed_at || new Date().toISOString() });
      toast.success(t('analysis.novelComplete'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('analysis.analysisFailed'));
    } finally {
      setRunningNovel(false);
    }
  };

  /** Build an issues payload enriched with chapter context for the repair modal. */
  const openRepair = (items) => {
    // Annotate each issue with chapter_id / chapter_number from the active chapter
    const enriched = items.map(it => ({
      ...it,
      chapter_id:     it.chapter_id     || activeChapterId  || undefined,
      chapter_number: it.chapter_number || activeChapter?.number || undefined,
      chapter_title:  it.chapter_title  || activeChapter?.title  || undefined,
    }));
    setRepairIssues(enriched);
    setRepairOpen(true);
  };

  return (
    <PanelFrame title={t('analysis.panelTitle')} icon={Activity} onClose={onClose}
                onMaximize={onMaximize} maximized={maximized} testId="analysis-panel"
                panelId="analysis"
                aiHint="analysis">
      <Tabs defaultValue="chapter" className="flex h-full flex-col">
        <TabsList className="m-2 h-7 w-fit bg-secondary/60 p-0.5">
          <TabsTrigger value="chapter" className="h-6 px-2 text-[13px] data-[state=active]:bg-background">
            {t('analysis.tabChapter')}
          </TabsTrigger>
          <TabsTrigger value="novel" className="h-6 px-2 text-[13px] data-[state=active]:bg-background">
            {t('analysis.tabNovel')}
          </TabsTrigger>
          <TabsTrigger value="structure" className="h-6 px-2 text-[13px] data-[state=active]:bg-background">
            Structure
          </TabsTrigger>
        </TabsList>

        {/* ── CHAPTER TAB ── */}
        <TabsContent value="chapter" className="flex-1 overflow-auto scroll-thin px-3 pb-3 m-0">
          <RunBar
            label={activeChapter
              ? t('analysis.chapterLabel', { number: activeChapter.number, title: activeChapter.title || t('common.untitled') })
              : t('analysis.noChapterSelected')}
            running={runningChapter}
            ranAt={chapterResult?.analyzed_at}
            onRun={runChapterAnalysis}
            testId="analysis-run-chapter"
          />
          {chapterResult ? (
            <>
              <Metrics m={chapterResult} />
              <SentimentChart
                data={[{ ch: activeChapter?.number ?? 1, label: `Ch ${activeChapter?.number}`, v: chapterResult.sentiment ?? 0 }]}
                titleKey="analysis.sentimentToneChapter"
              />
              <Issues
                items={chapterResult.issues || []}
                onFix={(items) => openRepair(items)}
              />
              <AnalysisHistory runs={chapterResult.history || []} />
            </>
          ) : (
            <EmptyState running={runningChapter} kind="chapter" />
          )}
        </TabsContent>

        {/* ── NOVEL TAB ── */}
        <TabsContent value="novel" className="flex-1 overflow-auto scroll-thin px-3 pb-3 m-0">
          <RunBar
            label={t('analysis.novelLabel', { count: chapters.length, title: project?.title || t('common.untitled') })}
            running={runningNovel}
            ranAt={novelResult?.analyzed_at}
            onRun={runNovelAnalysis}
            testId="analysis-run-novel"
          />
          {novelResult ? (
            <>
              <Metrics m={novelResult} />
              <SentimentChart
                data={sentimentData}
                titleKey={novelResult.chapter_sentiments
                  ? 'analysis.sentimentArcReal'
                  : 'analysis.sentimentArcNovel'}
              />
              <Issues
                items={novelResult.issues || []}
                onFix={(items) => openRepair(items)}
              />
            </>
          ) : (
            <EmptyState running={runningNovel} kind="novel" />
          )}
        </TabsContent>

        {/* ── STRUCTURE TAB ── */}
        <TabsContent value="structure" className="flex-1 overflow-auto scroll-thin px-3 pb-3 m-0" data-testid="analysis-structure-tab">
          <div className="mb-3 rounded-md border border-panel-border bg-card px-3 py-2.5">
            <p className="text-[13.5px] font-semibold text-foreground mb-1">Plot Element Readiness</p>
            <p className="text-[13px] text-muted-foreground leading-relaxed mb-2.5">
              Check your manuscript for the 6 structural narrative elements: Exposition, Inciting Incident, Rising Action, Climax, Falling Action, and Resolution. Missing elements are flagged and can be rebuilt with AI.
            </p>
            {project?.id && (
              <PlotReadinessPill projectId={project.id} stale={!!project?.plot_readiness_stale} />
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Issue Repair Modal */}
      <IssueRepairModal
        open={repairOpen}
        onOpenChange={setRepairOpen}
        issues={repairIssues}
        projectId={project?.id}
        initialChapterId={activeChapterId}
        chapterContent={activeChapter?.content || ''}
        onRepairAccepted={() => {
          // Reload analysis after a fix is committed
          runChapterAnalysis();
        }}
        onRerunAnalysis={runChapterAnalysis}
      />
    </PanelFrame>
  );
}

/* ── Sub-components ── */

function RunBar({ label, running, ranAt, onRun, testId }) {
  const { t } = useTranslation();
  const ts = ranAt
    ? new Date(ranAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;
  return (
    <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-panel-border bg-card px-2.5 py-1.5">
      <span className="truncate text-[13px] text-muted-foreground" title={label}>{label}</span>
      <div className="flex shrink-0 items-center gap-2">
        {ts && <span className="text-[13px] text-muted-foreground/60">{t('analysis.ran', { time: ts })}</span>}
        <Button
          size="sm"
          variant={ranAt ? 'outline' : 'default'}
          className="h-6 gap-1 px-2 text-[13px]"
          onClick={onRun}
          disabled={running}
          data-testid={testId}
        >
          {running
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <RefreshCw className="h-3 w-3" />}
          {running ? t('analysis.analyzing') : ranAt ? t('analysis.rerun') : t('analysis.runAnalysis')}
        </Button>
      </div>
    </div>
  );
}

function Metrics({ m }) {
  const { t } = useTranslation();

  // Core craft metrics
  const coreCells = [
    { k: t('analysis.metricContinuity'),  v: `${m.continuity ?? '—'}%`,      tone: (m.continuity ?? 0) >= 90 ? 'success' : 'warning',    icon: BookCheck },
    { k: t('analysis.metricPacing'),      v: `${m.pacing ?? '—'}%`,           tone: (m.pacing ?? 0)     >= 70 ? 'success' : 'warning',    icon: Gauge },
    { k: t('analysis.metricPlotholes'),   v: `${m.plotholes ?? '—'}`,         tone: (m.plotholes ?? 1)  === 0 ? 'success' : 'warning',    icon: AlertTriangle },
    { k: t('analysis.metricCliches'),     v: `${m.cliches ?? '—'}`,           tone: (m.cliches ?? 4)    <= 3  ? 'success' : 'warning',    icon: AlertTriangle },
    { k: t('analysis.metricReadability'), v: (m.readability ?? 0).toFixed(1), tone: 'primary',                                             icon: BookCheck },
  ];

  // Enhanced manuscript element scores (only if returned by the API)
  const hasDeep = m.character_score != null || m.plot_score != null;
  const deepCells = hasDeep ? [
    { k: 'Characters',  v: `${m.character_score ?? '—'}%`, tone: (m.character_score ?? 0) >= 75 ? 'success' : 'warning', icon: Users },
    { k: 'Plot',        v: `${m.plot_score ?? '—'}%`,      tone: (m.plot_score ?? 0)      >= 75 ? 'success' : 'warning', icon: GitBranch },
    { k: 'Theme',       v: `${m.theme_score ?? '—'}%`,     tone: (m.theme_score ?? 0)     >= 70 ? 'success' : 'warning', icon: Layers },
    { k: 'Dialogue',    v: `${m.dialogue_score ?? '—'}%`,  tone: (m.dialogue_score ?? 0)  >= 75 ? 'success' : 'warning', icon: MessageSquare },
    { k: 'Tension',     v: `${m.tension_score ?? '—'}%`,   tone: (m.tension_score ?? 0)   >= 70 ? 'success' : 'warning', icon: Flame },
  ] : [];

  const renderCells = (cells) => cells.map(c => {
    const Icon = c.icon;
    return (
      <div key={c.k} className="rounded-md border border-panel-border bg-card p-2">
        <div className="flex items-center justify-between">
          <span className="text-[12px] uppercase tracking-wider text-muted-foreground">{c.k}</span>
          <Icon className={cn('h-3 w-3', `text-${c.tone}`)} />
        </div>
        <div className="font-mono-stat text-[16px] font-semibold text-foreground">{c.v}</div>
      </div>
    );
  });

  return (
    <>
      <div className="mb-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        {renderCells(coreCells)}
      </div>
      {hasDeep && (
        <>
          <div className="mb-1 mt-2 text-[11px] uppercase tracking-wider text-muted-foreground px-0.5">Manuscript Elements</div>
          <div className="mb-3 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {renderCells(deepCells)}
          </div>
          <DeepNotes m={m} />
        </>
      )}
    </>
  );
}

// ── Deep element notes (character / plot / theme / dialogue / tension) ────────
function DeepNotes({ m }) {
  const sections = [
    { label: 'Characters', key: 'character_notes', icon: Users,        color: 'text-violet-500' },
    { label: 'Plot',       key: 'plot_notes',      icon: GitBranch,    color: 'text-blue-500'   },
    { label: 'Theme',      key: 'theme_notes',     icon: Layers,       color: 'text-amber-500'  },
    { label: 'Dialogue',   key: 'dialogue_notes',  icon: MessageSquare, color: 'text-teal-500'  },
    { label: 'Tension',    key: 'tension_notes',   icon: Flame,        color: 'text-rose-500'   },
  ].filter(s => Array.isArray(m[s.key]) && m[s.key].length > 0);

  if (!sections.length) return null;

  return (
    <div className="mb-3 space-y-2">
      {sections.map(({ label, key, icon: Icon, color }) => (
        <div key={key} className="rounded-md border border-panel-border bg-card">
          <div className="flex items-center gap-1.5 border-b border-panel-border px-2 py-1.5">
            <Icon className={cn('h-3 w-3', color)} />
            <span className="text-[12px] font-semibold text-foreground">{label}</span>
          </div>
          <ul className="divide-y divide-panel-border">
            {m[key].map((note, i) => (
              <li key={i} className="flex items-start gap-1.5 px-2 py-1.5 text-[13px] text-foreground/90 leading-relaxed">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                {note}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ── Story Beats ───────────────────────────────────────────────────────────────

const BEAT_META = {
  setup:             { label: 'Setup',             Icon: BookOpen,     bg: 'bg-sky-50',     border: 'border-sky-200',     iconBg: 'bg-sky-100',     iconC: 'text-sky-600',     tagC: 'text-sky-700'     },
  inciting_incident: { label: 'Inciting Incident', Icon: Sparkles,     bg: 'bg-amber-50',   border: 'border-amber-200',   iconBg: 'bg-amber-100',   iconC: 'text-amber-600',   tagC: 'text-amber-700'   },
  conflict:          { label: 'Conflict',           Icon: Flame,        bg: 'bg-rose-50',    border: 'border-rose-200',    iconBg: 'bg-rose-100',    iconC: 'text-rose-600',    tagC: 'text-rose-700'    },
  revelation:        { label: 'Revelation',         Icon: Sparkles,     bg: 'bg-amber-50',   border: 'border-amber-200',   iconBg: 'bg-amber-100',   iconC: 'text-amber-600',   tagC: 'text-amber-700'   },
  tension_build:     { label: 'Tension Build',      Icon: TrendingUp,   bg: 'bg-orange-50',  border: 'border-orange-200',  iconBg: 'bg-orange-100',  iconC: 'text-orange-600',  tagC: 'text-orange-700'  },
  rising_action:     { label: 'Rising Action',      Icon: TrendingUp,   bg: 'bg-orange-50',  border: 'border-orange-200',  iconBg: 'bg-orange-100',  iconC: 'text-orange-600',  tagC: 'text-orange-700'  },
  midpoint_shift:    { label: 'Midpoint Shift',     Icon: GitBranch,    bg: 'bg-violet-50',  border: 'border-violet-200',  iconBg: 'bg-violet-100',  iconC: 'text-violet-600',  tagC: 'text-violet-700'  },
  dark_moment:       { label: 'Dark Moment',        Icon: AlertTriangle,bg: 'bg-rose-50',    border: 'border-rose-200',    iconBg: 'bg-rose-100',    iconC: 'text-rose-600',    tagC: 'text-rose-700'    },
  climax:            { label: 'Climax',             Icon: Zap,          bg: 'bg-red-50',     border: 'border-red-200',     iconBg: 'bg-red-100',     iconC: 'text-red-600',     tagC: 'text-red-700'     },
  falling_action:    { label: 'Falling Action',     Icon: TrendingDown, bg: 'bg-indigo-50',  border: 'border-indigo-200',  iconBg: 'bg-indigo-100',  iconC: 'text-indigo-600',  tagC: 'text-indigo-700'  },
  resolution:        { label: 'Resolution',         Icon: CheckCircle2, bg: 'bg-emerald-50', border: 'border-emerald-200', iconBg: 'bg-emerald-100', iconC: 'text-emerald-600', tagC: 'text-emerald-700' },
  denouement:        { label: 'Denouement',         Icon: CheckCircle2, bg: 'bg-green-50',   border: 'border-green-200',   iconBg: 'bg-green-100',   iconC: 'text-green-600',   tagC: 'text-green-700'   },
  transition:        { label: 'Transition',         Icon: ArrowRight,   bg: 'bg-slate-50',   border: 'border-slate-200',   iconBg: 'bg-slate-100',   iconC: 'text-slate-500',   tagC: 'text-slate-600'   },
  emotional_shift:   { label: 'Emotional Shift',    Icon: MessageSquare,bg: 'bg-purple-50',  border: 'border-purple-200',  iconBg: 'bg-purple-100',  iconC: 'text-purple-600',  tagC: 'text-purple-700'  },
};
const DEFAULT_BEAT_META = BEAT_META.transition;

function TensionArrow({ dir }) {
  if (dir === 'rises') return <TrendingUp   className="h-2.5 w-2.5 text-rose-500" />;
  if (dir === 'falls') return <TrendingDown  className="h-2.5 w-2.5 text-emerald-500" />;
  return <Minus className="h-2.5 w-2.5 text-muted-foreground/50" />;
}

function StoryBeatsSection({ m }) {
  const chapterBeat = m?.chapter_beat;
  const beats       = m?.scene_beats || [];
  if (!chapterBeat && beats.length === 0) return null;

  const chMeta = BEAT_META[chapterBeat] || DEFAULT_BEAT_META;
  const ChIcon = chMeta.Icon;

  return (
    <div className="mb-3" data-testid="story-beats-section">
      {/* Chapter-level narrative beat badge */}
      {chapterBeat && (
        <div className={cn('mb-2 flex items-center gap-2.5 rounded-lg border px-3 py-2', chMeta.bg, chMeta.border)}>
          <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', chMeta.iconBg)}>
            <ChIcon className={cn('h-3.5 w-3.5', chMeta.iconC)} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Chapter Narrative Beat</p>
            <p className={cn('text-[13px] font-semibold', chMeta.tagC)}>{chMeta.label}</p>
          </div>
        </div>
      )}

      {/* Scene-by-scene timeline */}
      {beats.length > 0 && (
        <div className="rounded-md border border-panel-border bg-card overflow-hidden">
          <div className="flex items-center gap-1.5 border-b border-panel-border px-2 py-1.5">
            <Film className="h-3 w-3 text-muted-foreground" />
            <span className="text-[12px] font-semibold text-foreground">Scene Beats</span>
            <span className="ml-auto text-[10px] text-muted-foreground">
              {beats.length} scene{beats.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-panel-border">
            {beats.map((beat, i) => {
              const meta = BEAT_META[beat.beat_type] || DEFAULT_BEAT_META;
              const Icon = meta.Icon;
              return (
                <div key={i} className="flex items-start gap-2.5 px-2 py-2">
                  <div className={cn('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full', meta.iconBg)}>
                    <Icon className={cn('h-3 w-3', meta.iconC)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={cn('text-[10px] font-semibold uppercase tracking-wide', meta.tagC)}>
                        {meta.label}
                      </span>
                      <TensionArrow dir={beat.tension_change} />
                    </div>
                    <p className="text-[12px] text-foreground/90 leading-snug">{beat.summary}</p>
                    {beat.characters?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {beat.characters.map((c, ci) => (
                          <span key={ci} className="inline-flex rounded-full bg-muted/60 px-1.5 text-[10px] text-muted-foreground">
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ChapterBeatMap({ chapters, novelResult }) {
  const beatMap = novelResult?.chapter_beats;
  if (!beatMap || typeof beatMap !== 'object') return null;
  const entries = chapters
    .filter(ch => beatMap[ch.id])
    .map(ch => ({ ch, beat: beatMap[ch.id] }));
  if (entries.length === 0) return null;
  return (
    <div className="mb-3 rounded-md border border-panel-border bg-card overflow-hidden" data-testid="chapter-beat-map">
      <div className="flex items-center gap-1.5 border-b border-panel-border px-2 py-1.5">
        <Film className="h-3 w-3 text-muted-foreground" />
        <span className="text-[12px] font-semibold text-foreground">Chapter Beat Map</span>
        <span className="ml-auto text-[10px] text-muted-foreground">{entries.length} chapters</span>
      </div>
      <div className="divide-y divide-panel-border max-h-60 overflow-y-auto">
        {entries.map(({ ch, beat }) => {
          const meta = BEAT_META[beat] || DEFAULT_BEAT_META;
          const Icon = meta.Icon;
          return (
            <div key={ch.id} className="flex items-center gap-2 px-2 py-1.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                {ch.number}
              </span>
              <div className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full', meta.iconBg)}>
                <Icon className={cn('h-3 w-3', meta.iconC)} />
              </div>
              <p className="flex-1 min-w-0 text-[12px] text-foreground/90 truncate">
                {ch.title || `Chapter ${ch.number}`}
              </p>
              <span className={cn('text-[10px] font-semibold uppercase tracking-wide flex-shrink-0', meta.tagC)}>
                {meta.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SentimentChart({ data, titleKey }) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 rounded-md border border-panel-border bg-card">
      <div className="flex items-center justify-between border-b border-panel-border px-2 py-1.5">
        <span className="text-[13px] uppercase tracking-wider text-muted-foreground">{t(titleKey)}</span>
        <span className="font-mono-stat text-[13px] text-muted-foreground">−1 ↔ +1</span>
      </div>
      <div className="h-[110px] w-full">
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
            <defs>
              <linearGradient id="sent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="hsl(var(--primary))" stopOpacity={0.55} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <XAxis dataKey="ch" hide />
            <YAxis domain={[-1, 1]} tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))' }}
                   axisLine={false} tickLine={false} />
            <ReferenceLine y={0} stroke="hsl(var(--border))" />
            <Tooltip
              contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 6, fontSize: 10 }}
              formatter={(v) => [v.toFixed(2), t('analysis.sentimentLabel')]}
            />
            <Area type="monotone" dataKey="v" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#sent)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Issues({ items, onFix }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  const fixable = items.filter(it => it.tone !== 'success');
  return (
    <div className="rounded-md border border-panel-border bg-card">
      <div className="flex items-center justify-between border-b border-panel-border px-2 py-1.5">
        <span className="text-[13px] uppercase tracking-wider text-muted-foreground">
          {t('analysis.issuesTitle')}
        </span>
        {fixable.length > 0 && onFix && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 gap-1 px-2 text-[12px]"
            onClick={() => onFix(fixable)}
            data-testid="analysis-fix-issues-btn"
          >
            <Wrench className="h-3 w-3" />
            Fix Issues
          </Button>
        )}
      </div>
      <ul className="divide-y divide-panel-border">
        {items.map((it, idx) => (
          <li key={idx} className="flex items-start gap-2 px-2 py-1.5 text-[13.5px]">
            {it.tone === 'success'
              ? <CheckCircle2 className={cn('mt-0.5 h-3.5 w-3.5 shrink-0 text-success')} />
              : <AlertTriangle className={cn('mt-0.5 h-3.5 w-3.5 shrink-0',
                  it.tone === 'destructive' ? 'text-destructive' : 'text-warning')} />}
            <span className="flex-1 text-foreground/90">{it.text}</span>
            {it.tone !== 'success' && onFix && (
              <button
                type="button"
                onClick={() => onFix([it])}
                className="ml-1 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10 transition-colors"
                data-testid="analysis-fix-single-btn"
                title="Open in Issue Repair"
              >
                Fix →
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState({ running, kind }) {
  const { t } = useTranslation();
  if (running) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-[13.5px]">{t('analysis.readingKind', { kind })}</p>
        <p className="text-[13px] opacity-60">{t('analysis.takesTime')}</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-muted-foreground">
      <BookOpen className="h-8 w-8 opacity-30" />
      <p className="text-[13.5px]">{t('analysis.noAnalysisYet')}</p>
      <p className="text-[13px] opacity-60">{t('analysis.emptyHint')}</p>
    </div>
  );
}

function AnalysisHistory({ runs }) {
  const { t } = useTranslation();
  if (!runs?.length) return null;

  // Build spark line data: [oldest → current]. Include current placeholder
  // so the line always trends toward the active result.
  const sparkData = [...runs].reverse().map((r, i) => ({
    i,
    v: r.data?.continuity ?? null,
  })).filter(d => d.v !== null);

  // Trend indicator: compare oldest run to most recent
  const first = sparkData[0]?.v;
  const last  = sparkData[sparkData.length - 1]?.v;
  const diff  = first !== undefined && last !== undefined ? last - first : null;
  const TrendIcon = diff === null ? null : diff > 2 ? TrendingUp : diff < -2 ? TrendingDown : Minus;
  const trendColor = diff === null ? '' : diff > 2 ? 'text-emerald-600' : diff < -2 ? 'text-rose-500' : 'text-muted-foreground';

  return (
    <div className="mt-3 rounded-md border border-panel-border bg-card">
      <div className="flex items-center justify-between border-b border-panel-border px-2 py-1.5">
        <span className="flex items-center gap-1.5 text-[13px] uppercase tracking-wider text-muted-foreground">
          <Clock className="h-3 w-3" />
          {t('analysis.previousRuns', { count: runs.length })}
        </span>
        {TrendIcon && diff !== null && (
          <span className={cn('flex items-center gap-0.5 text-[13px] font-semibold', trendColor)}
                title={`${t('analysis.continuityLabel')} trend: ${diff > 0 ? '+' : ''}${diff.toFixed(0)}pp`}>
            <TrendIcon className="h-3 w-3" />
            {diff > 0 ? '+' : ''}{diff.toFixed(0)}
          </span>
        )}
      </div>
      {/* Micro spark line — continuity trend across saved runs */}
      {sparkData.length >= 2 && (
        <div className="h-[36px] w-full px-1 pt-1">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData}>
              <Line
                type="monotone"
                dataKey="v"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 3, fill: 'hsl(var(--primary))', strokeWidth: 0 }}
                isAnimationActive={false}
              />
              <YAxis domain={[0, 100]} hide />
              <Tooltip
                contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 4, fontSize: 10, padding: '2px 6px' }}
                formatter={(v) => [`${v}%`, t('analysis.continuityLabel')]}
                labelFormatter={() => ''}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <ul className="divide-y divide-panel-border">
        {runs.map((r, i) => {
          const ts = r.generated_at
            ? new Date(r.generated_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            : `Run ${runs.length - i}`;
          const d = r.data || {};
          return (
            <li key={i} className="flex items-center gap-3 px-2 py-1.5 text-[13px]">
              <span className="w-[100px] shrink-0 text-muted-foreground/70">{ts}</span>
              <span className="font-mono-stat text-[13px]">{d.continuity ?? '—'}{t('analysis.contAbbr')}</span>
              <span className="font-mono-stat text-[13px]">{d.pacing ?? '—'}{t('analysis.paceAbbr')}</span>
              <span className="font-mono-stat text-[13px] text-muted-foreground">{d.plotholes ?? '—'} {t('analysis.holesAbbr')}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
